<template>
  <nx-fullscreen-dialog
    @dismiss="dismiss"
    :active="active"
    :title="$t('message.relation.addFromZip')"
    :back="step > 1"
  >
    <template slot="toolbar-items">
      <v-btn
        v-if="step === 1"
        :disabled="!isValidZip"
        :loading="loading"
        @click="step = 2"
        color="accent"
        dark
        flat
      >{{ $t('message.wizard.nextBtn') }}
      </v-btn>
      <v-btn
        v-else-if="step === 2"
        :disabled="!isValidZip"
        :loading="loading"
        color="accent"
        @click="createPhotoGalleryWithMultipleImages"
        dark
        flat
      >{{ $t('message.wizard.createBtn') }}
      </v-btn>
      <v-btn
        v-else
        @click="emitInput"
        :loading="loading"
        :disabled="loading"
        color="accent"
        dark
        flat
      >{{ $t('message.wizard.continueBtn') }}</v-btn>
    </template>

    <v-container fluid class="nx-content-wizard-body">
      <v-layout wrap fill-height>
        <v-flex class="lg6 offset-lg3 md8 offset-md2 sm10 offset-sm1 xs2">

          <nx-form class="nx-form" v-if="step === 1">
            <nx-zip-selector
              @input="onZipSelected"
              @start="loading = true"
              @end="loading = false"
              :allowed-mimes="imageWizard.allowedMimes"
              :disabled="loading"
            />
          </nx-form>

          <div v-else-if="step === 2">
            <component
              :is="step2Component"
              v-model="imagesFile"
              :draft="draft"
              :disabled="loading"
            ></component>
          </div>

          <div v-else-if="step === 3">
            <v-list>
              <template v-for="(item, index) in imagesFile">
                <v-divider v-if="index" :key="index"></v-divider>
                <v-list-tile :key="item.blobUrl">
                  <div class="nx-normal-list-img mr-2">
                    <v-img
                      :src="item.blobUrl"
                      :alt="item.name"
                      width="80"
                      height="40"
                    ></v-img>
                  </div>
                  <v-list-tile-content>
                    {{ item.name }}
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-progress-circular
                      indeterminate
                      v-if="item.state === 'PENDING' || item.state === 'PROCESSING'"
                      :color="item.state === 'PENDING' ? null : 'green'"
                      :size="22"
                      :width="1"
                    ></v-progress-circular>
                    <v-icon color="green" v-else-if="item.state === 'SUCCESS'">done</v-icon>
                    <v-btn icon @click="retryImageCreation(item)" v-else :disabled="loading">
                      <v-icon color="red">error</v-icon>
                    </v-btn>
                  </v-list-tile-action>
                </v-list-tile>
              </template>
            </v-list>
          </div>

        </v-flex>
      </v-layout>
    </v-container>

    <nx-simple-dialog
      :title="$t('message.wizard.photoGallery.error')|capitalize "
      :shown="showRetryDialog"
      @close="showRetryDialog = false"
    >
      {{ $t('message.wizard.photoGallery.retry') }}
      <p v-for="(image,index) in failedImages" :key="index">
        <em>{{ image.name }}</em>
      </p>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="accent"
          flat
          @click="emitInput"
          :loading="loading"
        >{{ $t('message.wizard.continueBtn') }}
        </v-btn>
      </v-card-actions>
    </nx-simple-dialog>

  </nx-fullscreen-dialog>
</template>

<script>
import omit from 'lodash/omit';
import api from '@/modules/content/wizard/api';
import NxForm from '@/modules/form/Form.vue';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import NxSimpleDialog from '@/components/dialogs/SimpleDialog.vue';
import NxSortableList from '@/components/list/legacy/SortableList.vue';
import NxZipSelector from '@/modules/file/selectors/ZipSelector.vue';
import wizardConfig from '@/modules/content/wizard/config/wizard';
import { mapGetters } from 'vuex';
import { imageWizard } from '@/modules/content/config';

export default {
  name: 'nx-relation-editor-zip-uploader',
  inject: ['$wizardContext'],
  components: {
    NxZipSelector,
    [NxForm.name]: NxForm,
    [NxFullscreenDialog.name]: NxFullscreenDialog,
    [NxSimpleDialog.name]: NxSimpleDialog,
    [NxSortableList.name]: NxSortableList,
  },
  props: {
    active: Boolean,
    mainTaxonomyId: {
      type: Number,
      required: true,
    },
    imagesTitle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      imageWizard,
      createdImages: [],
      failedImages: [],
      imagesFile: [],
      loading: false,
      step: 1,
      showRetryDialog: false,
      draft: {},
    };
  },
  computed: {
    ...mapGetters(['context']),
    isValidZip() {
      return this.imagesFile.length > 0;
    },
    step2Component() {
      return wizardConfig.NxWizardPhotoGalleryStep2;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        if (value === true) {
          wizardConfig.fillDraft('zip-uploader', this.draft, this.$wizardContext);
        }
      },
    },
  },
  methods: {
    dismiss() {
      if (this.step > 1) {
        this.step -= 1;
      } else {
        this.$emit('dismiss');
      }
    },
    onZipSelected(files) {
      this.imagesFile = files.sort((a, b) => (a.name < b.name ? -1 : 1));
      this.step = 2;
    },
    async createPhotoGalleryWithMultipleImages() {
      this.step = 3;
      this.loading = true;
      for (let i = 0; i < this.imagesFile.length; i += 1) {
        await this.createImage(this.imagesFile[i]);
      }
      this.loading = false;

      if (this.failedImages.length === 0) {
        this.emitInput();
      } else {
        this.showRetryDialog = true;
      }
    },
    async createImage(image) {
      const imageData = omit(image, ['name', 'blobUrl', 'extension', 'file', 'state']);
      const payload = {
        ...imageData,
        autopublish: true,
        context: this.context,
        title: image.name,
        mainTaxonomyId: this.mainTaxonomyId,
      };

      const data = new FormData();
      data.append('file', image.file);
      data.append('payload', JSON.stringify(payload));
      image.state = 'PROCESSING';
      const result = await api.create(data, 'image');
      if (!result) {
        image.state = 'FAILED';
        this.failedImages.push(image);
      } else {
        image.state = 'SUCCESS';
        this.createdImages.push(result);
      }
    },
    emitInput() {
      this.$emit('input', this.createdImages, this.createdImages.map(image => image.id));
      this.$emit('dismiss');
    },
    async retryImageCreation(item) {
      const index = this.failedImages.indexOf(item);
      const failedItem = this.failedImages[index];
      await this.createImage(failedItem);
    },
  },
};
</script>
